import { formatters, selectors, type types } from "@fraction/shared";
import { motion } from "framer-motion";
import { forwardRef, type KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import Skeleton from "src/components/Skeleton";
import TextInput, { type TextInputProps } from "src/components/TextInput";
import Touchable from "src/components/Touchable";
import { RegularText } from "src/components/v1";
import { useDimensions, useGeolocation, useGoogle, useMergedRef, usePrevious } from "src/hooks";
import { LocationPin } from "src/icons";
import { cn } from "src/utilities/shadcnUtils";

const CONTAINER_CLASS = "min-w-[340px] max-[400px]:w-full max-[400px]:min-w-0 w-full max-w-full relative";
const SKELETON_CLASS = "h-[45.5px] w-[340px]";
const DROPDOWN_CLASS =
  "shadow-[2px_2px_8px_rgba(0,0,0,0.15)] dark:shadow-black/40 w-full overflow-y-scroll bg-white dark:bg-popover text-popover-foreground absolute z-[5] -mt-[18px] [&::-webkit-scrollbar]:hidden [&::-webkit-scrollbar]:[appearance:none] [&::-webkit-scrollbar]:w-0 [&::-webkit-scrollbar]:h-0 [-ms-overflow-style:none] [scrollbar-width:none]";
const SUGGESTION_CONTAINER_CLASS =
  "w-full min-h-[50px] py-3 pl-9 pr-[50px] bg-white dark:bg-popover hover:bg-green-100 dark:hover:bg-accent justify-start border-b-[0.5px] border-b-gray-400 dark:border-b-border border-solid";
const KEYBOARD_SELECTED_CLASS = "bg-green-200 dark:bg-accent";
const SUGGESTION_TEXT_CLASS = "text-sm text-left";

const SUGGESTION_HEIGHT = 58.85;

export type GoogleLocationTypes = "geocode" | "address" | "(regions)" | "(cities)";

export interface LocationInputProps extends Omit<TextInputProps, "value" | "onChange" | "onSelect"> {
  locationTypes?: GoogleLocationTypes[];
  onChange?: (location?: string) => void;
  onSelect?: (location?: types.ExtractedPropertyAddress) => void;
  value?: string | types.ExtractedPropertyAddress;
  className?: string;
  placeholder?: string;
  name?: string;
  loading?: boolean;
  InputComponent?: React.ComponentType<TextInputProps>;
}

interface Suggestion {
  label: string;
  value: google.maps.places.AutocompletePrediction;
}

const LocationInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, LocationInputProps>(
  (
    {
      locationTypes,
      onSelect,
      onChange,
      value,
      className,
      placeholder,
      label,
      name,
      disabled,
      loading,
      InputComponent = TextInput as unknown as React.ComponentType<TextInputProps>,
    }: LocationInputProps,
    forwardedRef
  ) => {
    const stringValue =
      typeof value === "object" ? formatters.property.formattedAddress(value, { premise: true }) : value;
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [inputValue, setInputValue] = useState(stringValue || "");
    const [keyboardIndex, setKeyboardIndex] = useState<number>(-1);
    const [getGeoLocation, setGetGeoLocation] = useState(false);
    const prevStringValue = usePrevious(stringValue);

    const { height, observe } = useDimensions();

    const mergedRef = useMergedRef(inputRef, forwardedRef);

    const geolocation = useGeolocation({ track: getGeoLocation });
    const { google, loaded } = useGoogle();

    const handleClickLocationPin = useCallback(() => {
      setGetGeoLocation(true);
    }, []);

    useEffect(() => {
      if ((stringValue || "") !== (prevStringValue || "")) {
        setInputValue(stringValue || "");
      }
    }, [setInputValue, prevStringValue, stringValue]);

    const handleChooseSuggestion = useCallback(
      async (suggestion: Suggestion) => {
        setSuggestions([]);
        setInputValue(suggestion.label);
        onChange?.(suggestion.label);

        const result = await google?.getPlaceDetails(suggestion?.value?.place_id);
        if (result) {
          onSelect?.(selectors.property.extractGoogleAddress(result));
        }
        // focus to reset the view so that we see the front of the text
        inputRef?.current?.focus();
      },
      [onSelect, setSuggestions, setInputValue, onChange, google]
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (!suggestions.length) {
          return;
        }

        switch (event.key) {
          case "ArrowUp":
            setKeyboardIndex((prev) => Math.min(Math.max(prev - 1, 0), suggestions.length));
            break;
          case "ArrowDown":
            setKeyboardIndex((prev) => Math.min(Math.max(prev + 1, 0), suggestions.length));
            break;
          case "Enter":
            if (keyboardIndex !== undefined && suggestions[keyboardIndex]) {
              handleChooseSuggestion(suggestions[keyboardIndex]);
            }
            break;
          default:
            break;
        }
      },
      [keyboardIndex, setKeyboardIndex, suggestions, handleChooseSuggestion]
    );

    const handleChangeInput = useCallback(
      async (text: string) => {
        setKeyboardIndex(-1);
        setInputValue(text);
        onChange?.(text);
        onSelect?.(undefined);

        if (!text.length) {
          setSuggestions([]);
          return;
        }

        const options: any = {
          input: text,
          location: { lat: geolocation?.latitude || 49.28273, lng: geolocation?.longitude || -123.120735 },
          radius: 500,
          types: locationTypes,
        };

        const predictions = await google?.getPlacePredictions(options);

        if (predictions) {
          setSuggestions(
            // The places autocomplete returns routes in addition to street addresses when we specify
            // the 'address' type.
            // This is counterintutitve, so we filter out ones that aren't actual addresses.
            (locationTypes?.[0] === "address"
              ? predictions.filter((prediction) =>
                  prediction.types.some((t) =>
                    ["street_address", "premise", "subpremise", "street_number"].includes(t)
                  )
                )
              : predictions
            ).map((prediction) => ({
              label: prediction.description,
              value: prediction,
            }))
          );
        }
      },
      [geolocation, locationTypes, onSelect, onChange, google]
    );

    if (!loaded || loading) {
      return <Skeleton className={SKELETON_CLASS} />;
    }

    const Component = InputComponent || TextInput;

    return (
      <div className={cn(CONTAINER_CLASS, className)}>
        <Component
          onKeyDown={handleKeyDown}
          autoComplete="off"
          disabled={disabled}
          type="text"
          name={name}
          ref={mergedRef}
          label={label}
          placeholder={placeholder}
          borderRadius={suggestions.length ? 0 : 10}
          className={CONTAINER_CLASS}
          value={inputValue}
          onChange={handleChangeInput}
          adornment={
            <Touchable onClick={handleClickLocationPin}>
              <LocationPin
                height={20}
                width={20}
                className="mr-[17px] text-[rgba(168,162,158,1)] dark:text-muted-foreground"
              />
            </Touchable>
          }
        />
        <motion.div
          transition={{ duration: 0.25 }}
          className={DROPDOWN_CLASS}
          // do the suggestions.length calculation as a fallback in case the dimension tracking doesn't work
          animate={{ height: Math.max(height, suggestions.length * SUGGESTION_HEIGHT) }}
        >
          <div ref={observe}>
            {!disabled &&
              suggestions.map((suggestion: Suggestion, index) => (
                <Touchable
                  key={suggestion.value?.place_id}
                  className={cn(
                    SUGGESTION_CONTAINER_CLASS,
                    index === keyboardIndex && KEYBOARD_SELECTED_CLASS
                  )}
                  onClick={() => handleChooseSuggestion(suggestion)}
                >
                  <RegularText className={SUGGESTION_TEXT_CLASS}>{suggestion.label}</RegularText>
                </Touchable>
              ))}
          </div>
        </motion.div>
      </div>
    );
  }
);

export default LocationInput;
